import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/verification-audit", label: "Verification Audit", icon: <ShieldCheck size={18} weight="duotone" />, testId: "nav-admin-verification-audit" }];

export default function AdminVerificationAuditPage() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [swap, setSwap] = useState(null);
  const [swapStarting, setSwapStarting] = useState(false);
  const pollRef = useRef(null);
  const swapPollRef = useRef(null);

  const fetchLatest = () => {
    api.get("/admin/verification-audit/latest")
      .then(({ data }) => {
        setAudit(data);
        setLoading(false);
        if (data && data.status === "running") {
          if (!pollRef.current) pollRef.current = setInterval(fetchLatest, 3000);
        } else if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      })
      .catch(() => setLoading(false));
  };

  const fetchSwap = () => {
    api.get("/admin/verification-audit/targeted-swap/latest")
      .then(({ data }) => {
        setSwap(data);
        if (data && data.status === "running") {
          if (!swapPollRef.current) swapPollRef.current = setInterval(fetchSwap, 3000);
        } else if (swapPollRef.current) {
          clearInterval(swapPollRef.current);
          swapPollRef.current = null;
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchLatest();
    fetchSwap();
    return () => {
      pollRef.current && clearInterval(pollRef.current);
      swapPollRef.current && clearInterval(swapPollRef.current);
    };
  }, []);

  const runAudit = async () => {
    setStarting(true);
    try {
      await api.post("/admin/verification-audit/run");
      toast.success("Live audit started — NumVerify, MillionVerifier, and DNC are being re-checked against every lead, right now. This can take 10+ minutes.");
      fetchLatest();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const runSwap = async () => {
    setSwapStarting(true);
    try {
      await api.post("/admin/verification-audit/targeted-swap");
      toast.success("Targeted swap started — pulling one fresh pool per category, verifying every candidate before anything is inserted or deleted. This can take 10-20 minutes.");
      fetchSwap();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSwapStarting(false);
    }
  };

  const running = audit?.status === "running";
  const swapRunning = swap?.status === "running";
  const totals = audit?.company_totals;

  return (
    <DashboardLayout navItems={navItems} moduleName="Verification Audit" moduleBackPath="/admin">
      <div data-testid="admin-verification-audit-page" className="max-w-6xl">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="Re-checks phone, email, and Do-Not-Call status live against every real lead in the system right now.">Live Lead Verification Audit</h1>
          <Button data-testid="verification-audit-run-button" onClick={runAudit} disabled={starting || running} title="Starts a fresh, live verification pass over every lead in the system. Can take 10+ minutes." className="bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40">
            {running ? "Running..." : starting ? "Starting..." : "Run Live Audit"}
          </Button>
        </div>
        <p className="text-igc-muted text-sm mb-6">
          Re-runs NumVerify, MillionVerifier, and DNC live, right now, against every real rep's actual leads — not a stored label. Use this any time a rep reports bad leads.
        </p>

        {loading && <p className="text-igc-muted text-sm" data-testid="verification-audit-loading">Loading...</p>}
        {!loading && !audit && <p className="text-igc-muted text-sm" data-testid="verification-audit-empty-state">No audit has been run yet. Click "Run Live Audit" to start one.</p>}

        {!loading && audit && (
          <div data-testid="verification-audit-results">
            {running && (
              <div className="bg-igc-surface border border-igc-gold/15 p-4 mb-6" data-testid="verification-audit-progress">
                <p className="text-igc-cream text-sm">Checking live: {audit.checked || 0} / {audit.total_leads || "?"} leads</p>
                <div className="w-full bg-igc-surfaceLight h-2 mt-2">
                  <div className="bg-igc-gold h-2" style={{ width: `${audit.total_leads ? Math.min(100, (audit.checked / audit.total_leads) * 100) : 0}%` }} />
                </div>
              </div>
            )}

            {audit.status === "completed" && (
              <>
                <p className="text-igc-muted text-xs mb-4" data-testid="verification-audit-completed-at">Completed {formatET(audit.completed_at, "MMM d, yyyy h:mm a")} ET · started by {audit.started_by}</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="verification-audit-company-totals">
                  {[["Total Checked", totals?.total, "text-igc-goldText", "Every lead checked in this audit run."], ["Fully Verified", totals?.fully_verified, "text-igc-success", "Leads that passed phone, email, and DNC checks."], ["Partial", totals?.partial, "text-amber-500", "Leads that passed some but not all checks."], ["Failed", totals?.failed, "text-igc-danger", "Leads that failed verification outright."], ["Not Run", totals?.not_run, "text-igc-muted", "Leads that haven't been verified yet."]].map(([label, value, color, hint]) => (
                    <div key={label} className="bg-igc-surface border border-igc-gold/15 p-4 text-center" title={hint}>
                      <p className={`text-2xl font-serif ${color}`}>{value ?? 0}</p>
                      <p className="text-igc-muted text-[10px] uppercase tracking-widest mt-1">{label}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-igc-surface border border-igc-gold/15 overflow-x-auto mb-6" data-testid="verification-audit-by-rep-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-igc-gold/15 text-igc-muted uppercase text-xs tracking-wide">
                        {["Rep Code", "Rep Name", "Total", "Fully Verified", "Partial", "Failed", "Not Run"].map((h) => (
                          <th key={h} className="text-left px-3 py-2.5">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(audit.by_rep || {}).sort((a, b) => a.rep_code.localeCompare(b.rep_code)).map((r) => (
                        <tr key={r.rep_code} className="border-b border-igc-gold/10 text-igc-cream" data-testid={`verification-audit-row-${r.rep_code}`}>
                          <td className="px-3 py-2">{r.rep_code}</td>
                          <td className="px-3 py-2">{r.rep_name}</td>
                          <td className="px-3 py-2">{r.total}</td>
                          <td className="px-3 py-2 text-igc-success">{r.fully_verified}</td>
                          <td className="px-3 py-2 text-amber-500">{r.partial}</td>
                          <td className="px-3 py-2 text-igc-danger">{r.failed}</td>
                          <td className="px-3 py-2 text-igc-muted">{r.not_run}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {audit.failed_leads?.length > 0 && (
                  <div className="bg-igc-surface border border-igc-gold/15 overflow-x-auto mb-6" data-testid="verification-audit-failed-leads-table">
                    <div className="flex items-center justify-between px-3 pt-3 pb-1">
                      <p className="text-igc-cream text-sm">Failed Leads ({audit.failed_leads.length})</p>
                      <Button data-testid="targeted-swap-run-button" onClick={runSwap} disabled={swapStarting || swapRunning} title="Pulls a fresh, verified replacement for every lead that failed, and deletes the bad ones. Cannot be undone." className="bg-igc-danger hover:bg-igc-danger/80 text-white disabled:opacity-40 text-xs h-8">
                        {swapRunning ? "Swap Running..." : swapStarting ? "Starting..." : "Run Targeted Swap"}
                      </Button>
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-igc-gold/15 text-igc-muted uppercase tracking-wide">
                          {["Rep", "Lead", "Phone", "NumVerify", "MillionVerifier", "DNC"].map((h) => (
                            <th key={h} className="text-left px-3 py-2">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {audit.failed_leads.map((f) => (
                          <tr key={f.lead_id} className="border-b border-igc-gold/10 text-igc-cream" data-testid={`verification-audit-failed-${f.lead_id}`}>
                            <td className="px-3 py-2">{f.rep_code}</td>
                            <td className="px-3 py-2">{f.name}</td>
                            <td className="px-3 py-2 text-igc-muted">{f.phone}</td>
                            <td className="px-3 py-2">{f.numverify}</td>
                            <td className="px-3 py-2">{f.millionverifier}</td>
                            <td className="px-3 py-2">{f.dnc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {swap && (
                  <div className="bg-igc-surface border border-igc-gold/15 p-4 mt-6" data-testid="targeted-swap-panel">
                    <p className="text-igc-cream text-sm mb-2">Targeted Swap — {swap.status}</p>
                    {swapRunning && (
                      <div className="text-igc-muted text-xs space-y-1" data-testid="targeted-swap-progress">
                        <p>Pulling one fresh pool per category and verifying every candidate before writing anything — this can take a while.</p>
                        {swap.progress && Object.entries(swap.progress).map(([code, cats]) => (
                          <p key={code}>{code}: {Object.entries(cats).map(([cat, v]) => `${cat} ${v}`).join(", ")}</p>
                        ))}
                      </div>
                    )}
                    {swap.status === "completed" && (
                      <div data-testid="targeted-swap-completed">
                        <p className="text-igc-success text-sm mb-2">{swap.total_replaced} leads replaced, {swap.deleted} bad leads deleted.</p>
                        <div className="text-xs text-igc-muted space-y-1">
                          {Object.entries(swap.replacements_by_rep || {}).map(([code, v]) => (
                            <p key={code} data-testid={`targeted-swap-rep-${code}`}>{code} {v.rep_name}: +{v.trust} trust, +{v.funding} funding</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {swap.status === "aborted_shortfall" && (
                      <div data-testid="targeted-swap-shortfall">
                        <p className="text-igc-danger text-sm mb-2">Aborted — some rep+category combos didn't get enough clean candidates. Nothing was deleted, nothing was left half-done.</p>
                        {swap.shortfalls?.map((s, i) => (
                          <p key={i} className="text-xs text-igc-muted">{s.rep_code} {s.category}: needed {s.needed}, got {s.got} (short by {s.short_by})</p>
                        ))}
                      </div>
                    )}
                    {swap.status === "failed" && <p className="text-igc-danger text-sm" data-testid="targeted-swap-failed">Failed: {swap.error}</p>}
                  </div>
                )}
              </>
            )}

            {audit.status === "failed" && <p className="text-igc-danger text-sm" data-testid="verification-audit-failed-state">Audit failed: {audit.error}</p>}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
